import { ApiError } from '@discovr/common';
import { errorMessage } from './errorCopy';
import { renamedConflictMessage } from './shopifyRenamed';

const plural = (n: number, one: string, many: string) => (n === 1 ? one : many);

/**
 * Copy for editing a deactivated code and for the "a code is being
 * edited" outcomes of the other Shopify writes. Every line describes what the
 * backend reported, never what the form hoped for.
 */
export const shopifyEditCopy = {
  offBanner:
    'This code is deactivated. Saving keeps it deactivated, unless you change its dates: an end date in the future (or no end date) makes Shopify activate it again, or schedule it if the start date is also in the future.',
  saved: {
    expired: 'Saved. The code is still deactivated.',
    active: 'Saved. Shopify has activated the code.',
    scheduled: 'Saved. Shopify has scheduled the code to start on its start date.',
  },
  errors: {
    generic: 'Couldn’t update discount code',
    dataRemoved:
      'This campaign’s Shopify data was removed after the store was uninstalled, so nothing was changed.',
    editInProgress:
      'This code is being edited, or your change is still being confirmed. Check the list, then try again if needed.',
    saveUnconfirmed:
      'Shopify didn’t confirm the save in time, so it may have gone through. The list has been refreshed; check it, then save again if needed.',
    editStale: 'This code changed since you opened it. Close and reopen to see the latest.',
    endNotAfterStart: 'The end must be after the start. Pick a later end, or an earlier start.',
    endNotFuture: 'The end must be in the future. Pick a later end date or time.',
    replaceEndPassed:
      'This code’s end date has passed, so a new code can’t copy it. Edit the discount and set a later end date, then change the code.',
    startDateRequired: 'A deactivated code needs a start date. Pick one to save.',
    gone: 'This code no longer exists, so there is nothing to edit. The list has been refreshed.',
    campaignCodeLive:
      'Another code is this campaign’s live code, so these dates can’t activate this one. Deactivate the live code first, or leave the dates as they are.',
    creatorCodeLive:
      'This creator already has another live code, so these dates can’t activate this one. Deactivate that code first, or leave the dates as they are.',
    codeTaken: 'This code is already used in your Shopify store. Choose a different code.',
    termsNotApplied:
      'Shopify kept this code’s usage limit or minimum purchase, so it is still in place. Your other changes were saved. Remove it in your Shopify admin, then refresh.',
  },
  live: {
    lead: (campaignWide: boolean) =>
      campaignWide ? 'A creator on this campaign is live.' : 'This creator is live.',
    impact: (code: string) =>
      `Shoppers using ${code} get the new terms as soon as you save. The code and its link stay the same.`,
    linksKept: (n: number) =>
      `${n} live creator link${plural(n, '', 's')} kept ${plural(n, 'its', 'their')} current page.`,
  },
  nativeCodes: (n: number) =>
    `This Shopify discount has ${n} codes. Changes here apply to all of them.`,
  valueLocked: (code: string) =>
    `${code} includes its discount value, so the discount type and value can’t change.`,
  valueInCodeRefused:
    'This code includes its discount value, so the discount type and value can’t change.',
  invalidateMany: {
    nothingChanged:
      'Nothing was changed. The selection may be out of date, so the list has been refreshed.',
  },
} as const;

/** The toast for a saved off-code edit; undefined for an edit of an assigned
 *  code, which carries no `shopify_state`. */
export function editSavedMessage(state?: 'active' | 'scheduled' | 'expired'): string | undefined {
  return state ? shopifyEditCopy.saved[state] : undefined;
}

export interface EditConfigFailure {
  message: string;
  /** The row changed or vanished under the form; reopening it would show stale data. */
  closeDialog: boolean;
  refreshList: boolean;
  /** A code on the discount spells out its value, so the value fields lock. */
  lockValue?: boolean;
  /** The form was opened on an older version of the coupon; saving again would repeat the 409. */
  stale?: boolean;
}

/** The row left the state a form was opened in (deleted, replaced, reactivated, taken over by Shopify). */
export const ROW_CHANGED_CODES: ReadonlySet<string> = new Set([
  'CONFLICT',
  'SHOPIFY_COUPON_REPLACED',
  'SHOPIFY_MANAGED_IN_SHOPIFY',
]);

// The backend's two date-rule 400s; the first is also the deactivated edit's wording.
const END_NOT_AFTER_START = new Set([
  'end date must be after start date',
  'end_date must be after start_date',
]);
const END_NOT_FUTURE = new Set(['end date must be in the future', 'end_date must be in the future']);

/** Clear copy for a save the backend refused on the dates; undefined for any other error. */
export function dateRuleMessage(err: unknown): string | undefined {
  if (!(err instanceof ApiError) || err.httpStatus !== 400) return undefined;
  if (END_NOT_AFTER_START.has(err.message)) return shopifyEditCopy.errors.endNotAfterStart;
  if (END_NOT_FUTURE.has(err.message)) return shopifyEditCopy.errors.endNotFuture;
  return undefined;
}

// A gateway failure with no backend envelope, or a client timeout, may have reached Shopify; offline and enveloped errors are definite.
function isUnconfirmedTransport(err: ApiError): boolean {
  if (err.code === 'UNKNOWN') return [502, 503, 504].includes(err.httpStatus);
  return err.code === 'NETWORK_ERROR' && /timeout/i.test(err.message);
}

export function editConfigFailure(err: unknown): EditConfigFailure {
  const copy = shopifyEditCopy.errors;
  if (err instanceof ApiError) {
    if (err.code === 'SHOPIFY_DATA_REMOVED') {
      return { message: copy.dataRemoved, closeDialog: true, refreshList: true };
    }
    if (err.code === 'SHOPIFY_EDIT_IN_PROGRESS') {
      // The edit may have been applied before the claim was lost, so the list can already differ.
      return { message: copy.editInProgress, closeDialog: false, refreshList: true };
    }
    if (err.code === 'SHOPIFY_TERMS_NOT_APPLIED') {
      // The other changes were saved and the mirror now shows what Shopify holds, so the form is out of date.
      return { message: copy.termsNotApplied, closeDialog: true, refreshList: true };
    }
    if (err.code === 'SHOPIFY_EDIT_STALE') {
      return { message: copy.editStale, closeDialog: false, refreshList: true, stale: true };
    }
    if (err.code === 'SHOPIFY_SAVE_UNCONFIRMED') {
      return { message: copy.saveUnconfirmed, closeDialog: false, refreshList: true };
    }
    if (isUnconfirmedTransport(err)) {
      return { message: copy.saveUnconfirmed, closeDialog: false, refreshList: true };
    }
    if (err.httpStatus === 404) {
      return { message: copy.gone, closeDialog: true, refreshList: true };
    }
    if (err.httpStatus === 409 && err.code === 'SHOPIFY_CODE_TAKEN') {
      return { message: copy.codeTaken, closeDialog: false, refreshList: false };
    }
    if (err.code === 'SHOPIFY_VALUE_IN_CODE') {
      // The server's text suggests Change code, which a live creator can't use.
      return { message: shopifyEditCopy.valueInCodeRefused, closeDialog: false, refreshList: false, lockValue: true };
    }
    if (err.code === 'SHOPIFY_CAMPAIGN_CODE_LIVE') {
      return { message: copy.campaignCodeLive, closeDialog: false, refreshList: false };
    }
    if (err.code === 'SHOPIFY_CREATOR_CODE_LIVE') {
      return { message: copy.creatorCodeLive, closeDialog: false, refreshList: false };
    }
    const renamed = renamedConflictMessage(err);
    if (renamed) return { message: renamed, closeDialog: true, refreshList: true };
    if (ROW_CHANGED_CODES.has(err.code)) {
      return { message: errorMessage(err, copy.generic), closeDialog: true, refreshList: true };
    }
  }
  return {
    message: dateRuleMessage(err) ?? errorMessage(err, copy.generic),
    closeDialog: false,
    refreshList: false,
  };
}

export interface OutcomeMessage {
  kind: 'success' | 'warning' | 'error';
  message: string;
}

const codes = (n: number) => `${n} code${n === 1 ? '' : 's'}`;

/** The toast for a bulk deactivate, from the counts the server returned. */
export function invalidateManyMessage(res: { invalidated: number; failed: number }): OutcomeMessage {
  const { invalidated, failed } = res;
  const past = 'deactivated';
  const Past = 'Deactivated';
  if (invalidated === 0 && failed === 0) {
    return { kind: 'warning', message: shopifyEditCopy.invalidateMany.nothingChanged };
  }
  if (failed === 0) return { kind: 'success', message: `${Past} ${codes(invalidated)}` };
  if (invalidated === 0) {
    return {
      kind: 'error',
      message:
        failed === 1
          ? `The code couldn’t be ${past} and may still work at checkout.`
          : `No codes were ${past}. All ${failed} may still work at checkout.`,
    };
  }
  return {
    kind: 'warning',
    message: `${Past} ${invalidated} of ${invalidated + failed} codes. The other ${failed} ${failed === 1 ? 'was' : 'were'} not ${past} and may still work at checkout. One may be being edited; try again in a couple of minutes.`,
  };
}

/** The creator's other live code named by a 409 SHOPIFY_CREATOR_CODE_LIVE from
 *  Reactivate; undefined for any other error. */
export function creatorLiveCode(err: unknown): string | undefined {
  if (!(err instanceof ApiError) || err.code !== 'SHOPIFY_CREATOR_CODE_LIVE') return undefined;
  const details = err.details as { code?: unknown } | undefined;
  return typeof details?.code === 'string' && details.code.trim() !== '' ? details.code : undefined;
}
